import React, { useState } from 'react';
import { DashboardWidget, WidgetScopeControl } from './DashboardWidgets';
import { TIME_SCOPES } from './timeScopes';
import { useScopedStatistics } from './useScopedStatistics';
import { TransactionsGraph } from './TransactionsGraph';

// DJB-194 (was DJB-142 "Product Sales"): payment-type transaction counts from
// the count-by-type endpoint, re-queried whenever the time scope at the top of
// the widget changes. The backend (DJB-193) returns [{paymentType: <enum
// name>, count}] for every payment type, now including payment_type 0
// (PAYCONIQ → Mobile Pay) and 1 (CASH). Each block shows its admin-facing
// label, the centered count as "1000x" and a clarification caption.
export const TRANSACTION_TYPES = [
  { enumName: 'FREE', label: 'Tokens', caption: 'Tokens consumed' },
  { enumName: 'BONUS', label: 'Daily Bonus', caption: 'Bonus Tiers reached' },
  { enumName: 'TRACK_BUY', label: 'Tracks', caption: 'Tracks requested' },
  { enumName: 'SKIP_QUEUE', label: 'Skip Queue', caption: "Queue's skipped" },
  { enumName: 'SHUFFLE_LISTBUY', label: 'Party Shuffle', caption: 'Multi-track requests' },
  { enumName: 'PAYCONIQ', label: 'Mobile Pay', caption: 'Mobile Pay transactions' },
  { enumName: 'CASH', label: 'Cash', caption: 'Cash Transactions' },
];

// DJB-195 r2 / DJB-212: every bounded scope enables the graph (and its series
// query) — the series endpoint takes any from→now range and picks the bucket
// granularity itself (hour ≤ 3d, day ≤ 92d, month ≤ 2y), so YTD and 1yr plot
// as daily/monthly buckets. Lifetime spans years of yearly buckets, which is
// too coarse for a line and too heavy to zero-fill — it shows a note instead.
export const GRAPH_SCOPE_KEYS = ['1d', '3d', '7d', '30d', 'ytd', '1y'];

// One Transactions block: name up top, the "<count>x" amount centered in the
// block, clarification caption at the bottom center. DJB-195 r2 makes the
// blocks a single-select group: clicking one selects it (blue) and plots its
// series as the one line in the graph below; clicking it again deselects.
export const TransactionBlock = ({ label, caption, count, loading, selected, onToggle }) => (
  <button
    type="button"
    className={`transaction-block ${selected ? 'is-selected' : ''}`.trim()}
    aria-pressed={selected}
    onClick={onToggle}
  >
    <span className="transaction-block-label">{label}</span>
    <span className="transaction-block-value">{loading ? '…' : `${count}x`}</span>
    <span className="transaction-block-caption">{caption}</span>
  </button>
);

export const TransactionsWidget = () => {
  const [scope, setScope] = useState('7d');
  // DJB-195 r2: at most one payment type plots its line; none selected at start
  const [selectedType, setSelectedType] = useState(null);
  const graphEnabled = GRAPH_SCOPE_KEYS.includes(scope);
  const counts = useScopedStatistics('/statistics/payments/count-by-type', scope);
  const series = useScopedStatistics('/statistics/payments/series-by-type', scope, {
    enabled: graphEnabled,
  });

  const countFor = (enumName) => {
    const entry = (counts.data || []).find(item => item.paymentType === enumName);
    return entry ? entry.count : 0;
  };

  const toggleType = (enumName) =>
    setSelectedType((prev) => (prev === enumName ? null : enumName));

  return (
    <DashboardWidget
      title="Transactions"
      variant="long"
      className="transactions-widget"
      // DJB-169: the time scope sits top-right in the title bar
      actions={
        <WidgetScopeControl
          label="Transactions time scope"
          options={TIME_SCOPES}
          value={scope}
          onChange={setScope}
        />
      }
    >
      {counts.error ? (
        <div className="widget-empty-note">Transactions are unavailable right now.</div>
      ) : (
        <>
          <div className="transaction-blocks">
            {TRANSACTION_TYPES.map(({ enumName, label, caption }) => (
              <TransactionBlock
                key={enumName}
                label={label}
                caption={caption}
                count={countFor(enumName)}
                loading={counts.loading}
                selected={selectedType === enumName}
                onToggle={() => toggleType(enumName)}
              />
            ))}
          </div>
          {graphEnabled ? (
            <TransactionsGraph
              data={series.data}
              loading={series.loading}
              error={series.error}
              selectedType={
                TRANSACTION_TYPES.find((type) => type.enumName === selectedType) || null
              }
              scopeKey={scope}
            />
          ) : (
            <div className="widget-empty-note">
              The graph is only available for scopes up to 1yr.
            </div>
          )}
        </>
      )}
    </DashboardWidget>
  );
};

export default TransactionsWidget;
